/**
 * Toast Zustand store + imperative API.
 *
 * Call `toast.show()`, `toast.success()`, etc. from anywhere.
 * The root layout renders `<ToastContainer />` which consumes
 * the store and renders visible toasts.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §10C
 */

import { create } from 'zustand'

// ─── Types ──────────────────────────────────────────────────────────────────

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface ToastConfig {
  /** Display message */
  message: string
  /** Optional secondary line */
  description?: string
  /** Visual style */
  variant?: ToastVariant
  /** Auto-dismiss duration in ms (default: 3500, 0 = persistent) */
  duration?: number
  /** Optional action button */
  action?: { label: string; onPress: () => void }
  /** Unique ID (auto-generated if not provided) */
  id?: string
}

export interface ToastItem extends Required<Pick<ToastConfig, 'id' | 'variant' | 'duration'>> {
  message: string
  description?: string
  action?: { label: string; onPress: () => void }
  createdAt: number
}

// ─── Store ──────────────────────────────────────────────────────────────────

interface ToastStore {
  toasts: ToastItem[]
  add: (config: ToastConfig) => string
  dismiss: (id: string) => void
  dismissAll: () => void
}

const MAX_VISIBLE = 3

let counter = 0
function generateId(): string {
  counter += 1
  return `toast-${Date.now()}-${counter}`
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  add: (config) => {
    const id = config.id ?? generateId()
    const variant = config.variant ?? 'default'
    // Errors persist until the user dismisses them — UX best practice that
    // avoids users missing a critical signal because the toast auto-faded
    // before they read it. Successes/info/warnings still auto-dismiss after
    // 3.5s. Caller can override per-toast via explicit `duration`.
    const defaultDuration = variant === 'error' ? 0 : 3500
    const item: ToastItem = {
      id,
      message: config.message,
      description: config.description,
      variant,
      duration: config.duration ?? defaultDuration,
      action: config.action,
      createdAt: Date.now(),
    }

    set((state) => {
      const next = [...state.toasts, item]
      // Auto-dismiss oldest if over max
      if (next.length > MAX_VISIBLE) {
        return { toasts: next.slice(next.length - MAX_VISIBLE) }
      }
      return { toasts: next }
    })

    return id
  },

  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },

  dismissAll: () => {
    set({ toasts: [] })
  },
}))

// ─── Imperative API ─────────────────────────────────────────────────────────

export const toast = {
  show: (message: string, config?: Omit<ToastConfig, 'message'>) =>
    useToastStore.getState().add({ message, ...config }),

  success: (message: string, config?: Omit<ToastConfig, 'message' | 'variant'>) =>
    useToastStore.getState().add({ message, variant: 'success', ...config }),

  error: (message: string, config?: Omit<ToastConfig, 'message' | 'variant'>) =>
    useToastStore.getState().add({ message, variant: 'error', ...config }),

  warning: (message: string, config?: Omit<ToastConfig, 'message' | 'variant'>) =>
    useToastStore.getState().add({ message, variant: 'warning', ...config }),

  info: (message: string, config?: Omit<ToastConfig, 'message' | 'variant'>) =>
    useToastStore.getState().add({ message, variant: 'info', ...config }),

  dismiss: (id: string) => useToastStore.getState().dismiss(id),

  dismissAll: () => useToastStore.getState().dismissAll(),
}
